import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from '../../utils/modalUtils';
import authApiService from '../../services/authServices';
import { normalizeTiebreakScores } from '../../utils/scorecardDisplay';

const PostResult = () => {
    const [startDate, setStartDate] = useState(new Date());
    const [showModal, setShowModal] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId")
    const userId = searchParams.get("userId");
    const opponentName = searchParams.get("full_name");
    const editMode = searchParams.get("editMode") === "true"; // Edit mode check
    const returnPage = searchParams.get("returnPage"); // From where user came for edit
    const returnMemberId = searchParams.get("returnMemberId"); // Original member whose records were viewed
    const returnRecordType = searchParams.get("recordType") || "overall";
    const scheduleUserId = searchParams.get("scheduleUserId");
    const seasonId = searchParams.get("seasonId");
    const [userInfo, setUserInfo] = useState({ firstName: "", lastName: "", userId: null, role: null });
    const [matchPlayers, setMatchPlayers] = useState({
        player1: null,
        player2: null,
        golfer1Id: null,
        golfer2Id: null,
    }); // Match golfer1/golfer2 order (scores p1* / p2*)

    const [matchSeasonName, setMatchSeasonName] = useState("");
    const [formData, setFormData] = useState({
        bDefault: "false",
        puttoff_winner: "",
        bVerify: "false",
        opponentRetired: "false",
        scores: {
            p1set1: 0,
            p1set2: 0,
            p1set3: 0,
            p2set1: 0,
            p2set2: 0,
            p2set3: 0,
            p1set1sup: 0,
            p1set2sup: 0,
            p1set3sup: 0,
            p2set1sup: 0,
            p2set2sup: 0,
            p2set3sup: 0
        }
    });


    useEffect(() => {
        const token = localStorage.getItem("accessToken");
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split(".")[1])); // Decode JWT
                setUserInfo({
                    firstName: payload.firstName || "",
                    lastName: payload.lastName || "",
                    userId: payload.userId || payload.id || null,
                    role: payload.role || null
                });
            } catch (error) {
                console.error("Error decoding access token:", error);
            }
        }
    }, []);

    // Fetch existing match data to populate player names & scores
    useEffect(() => {
        if (matchId && userId) {
            const preserveDraft = Boolean(location.state?.draftFormData);
            fetchExistingMatchData(preserveDraft);
        }
    }, [matchId, userId, location.state]);

    // When user comes back from preview page, restore unsaved draft.
    useEffect(() => {
        const draftFormData = location.state?.draftFormData;
        const draftStartDate = location.state?.draftStartDate;
        if (draftFormData) {
            setFormData(draftFormData);
        }
        if (draftStartDate) {
            const parsed = new Date(draftStartDate);
            if (!Number.isNaN(parsed.getTime())) {
                setStartDate(parsed);
            }
        }
    }, [location.state]);

    const fetchExistingMatchData = async (preserveDraftForm = false) => {
        try {
            const response = await authApiService.get(`viewResults?matchId=${matchId}&userId=${userId}`);
            console.log("Edit mode - Match data response:", response);
            
            if (response && response.data && response.data.length > 0) {
                const existingData = response.data[0];
                const golfer1Id = existingData.golfer1;
                const golfer2Id = existingData.golfer2;
                // Map names strictly from API response golfer1/golfer2 rows.
                // (Admin edit mein URL `full_name` mismatch ho sakta hai, isliye fallback nahi lenge.)
                const rowForGolfer1 = response.data.find(r => String(r.user_id) === String(golfer1Id));
                const rowForGolfer2 = response.data.find(r => String(r.user_id) === String(golfer2Id));
                const p1Row = rowForGolfer1 || response.data[0];
                const p2Row =
                    rowForGolfer2 ||
                    response.data.find(r => String(r.user_id) !== String(golfer1Id)) ||
                    response.data[1];

                const player1Name = p1Row
                    ? `${p1Row.first_name || ''} ${p1Row.last_name || ''}`.trim()
                    : "";
                const player2Name = p2Row
                    ? `${p2Row.first_name || ''} ${p2Row.last_name || ''}`.trim()
                    : "";

                console.log("Player names (from API):", { player1Name, player2Name, golfer1Id, golfer2Id });

                setMatchPlayers({
                    player1: player1Name || "Player 1",
                    player2: player2Name || "Player 2",
                    golfer1Id: golfer1Id != null ? Number(golfer1Id) : null,
                    golfer2Id: golfer2Id != null ? Number(golfer2Id) : null,
                });
                setMatchSeasonName(existingData.season_name || "");
                
                if (!preserveDraftForm) {
                    // Pre-fill form with existing data
                    setFormData({
                        bDefault: existingData.default_winner ? "true" : "false",
                        puttoff_winner: existingData.puttoff_winner || "",
                        bVerify: "false",
                        opponentRetired: existingData.opponentRetired ? "true" : "false",
                        scores: {
                            p1set1: existingData.p1set1 || 0,
                            p1set2: existingData.p1set2 || 0,
                            p1set3: existingData.p1set3 || 0,
                            p2set1: existingData.p2set1 || 0,
                            p2set2: existingData.p2set2 || 0,
                            p2set3: existingData.p2set3 || 0,
                            p1set1sup: existingData.p1set1sup || 0,
                            p1set2sup: existingData.p1set2sup || 0,
                            p1set3sup: existingData.p1set3sup || 0,
                            p2set1sup: existingData.p2set1sup || 0,
                            p2set2sup: existingData.p2set2sup || 0,
                            p2set3sup: existingData.p2set3sup || 0
                        }
                    });

                    // Set date if available
                    if (existingData.played || existingData.play_by) {
                        setStartDate(new Date(existingData.played || existingData.play_by));
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching existing match data:", error);
            Modal.fire("Error", "Failed to load existing match data.", "error");
        }
    };

    // Function to open modal
    // const openModal = () => {
    //     setShowModal(true);
    //     // Also try Bootstrap way as fallback
    //     setTimeout(() => {
    //         if (window.$ && window.$('#exampleModal').length && window.$('#exampleModal').modal) {
    //             window.$('#exampleModal').modal('show');
    //         }
    //     }, 100);
    // };

    // Function to close modal
    const closeModal = () => {
        setShowModal(false);
        // Also try Bootstrap way as fallback
        if (window.$ && window.$('#exampleModal').length && window.$('#exampleModal').modal) {
            window.$('#exampleModal').modal('hide');
        }
    };


    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        if (name.includes('scores.')) {
            const scoreKey = name.split('.')[1];
            const parsedValue = value === "" ? 0 : parseInt(value, 10) || 0;
            let normalizedValue = parsedValue;

            // Normal set score can only be 0..7 (games per set).
            if (/^p[12]set[123]$/.test(scoreKey)) {
                if (value !== "" && !Number.isNaN(parsedValue) && parsedValue > 7) {
                    Modal.fire(
                        "Invalid score",
                        "Set games cannot be greater than 7.",
                        "warning"
                    );
                }
                normalizedValue = Math.max(0, Math.min(7, parsedValue));
            }

            // Tiebreak scores are numeric and non-negative.
            if (/^p[12]set[123]sup$/.test(scoreKey)) {
                normalizedValue = Math.max(0, parsedValue);
            }

            setFormData(prev => ({
                ...prev,
                scores: {
                    ...prev.scores,
                    [scoreKey]: normalizedValue

                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === "checkbox" ? (checked ? "true" : "false") : value
            }));
        }
    };

    const handleScoreFocus = (e) => {
        e.target.select();
    };

    const validateScores = (scores, opponentRetired) => {
        let p1SetsWon = 0;
        let p2SetsWon = 0;
        const isOpponentRetired = opponentRetired === "true";

        // Legitimate completed set (no tiebreak):
        // 6-0..6-4, or 7-5 only. A "7" is never valid with 0-4 (e.g. 7-4).
        // 7-6 must go through the tiebreak branch below.
        const isValidStraightSetWin = (winner, loser, winnerSup, loserSup) => {
            if ((winnerSup || 0) > 0 || (loserSup || 0) > 0) return false;
            if (winner === 6 && loser >= 0 && loser <= 4) return true;
            if (winner === 7 && loser === 5) return true;
            return false;
        };

        for (let i = 1; i <= 3; i++) {
            const p1Score = scores[`p1set${i}`];
            const p2Score = scores[`p2set${i}`];
            const p1Super = scores[`p1set${i}sup`];
            const p2Super = scores[`p2set${i}sup`];

            // Skip validation for Set 3 if a player has already won 2 sets
            if (i === 3 && (p1SetsWon >= 2 || p2SetsWon >= 2)) {
                if (p1Score > 0 || p2Score > 0 || p1Super > 0 || p2Super > 0) {
                    return "Set 3 is not required if a player wins in two sets.";
                }
                continue; // Skip further validation for Set 3
            }

            // Check for negative scores
            if (p1Score < 0 || p2Score < 0 || p1Super < 0 || p2Super < 0) {
                return "Scores cannot be negative.";
            }

            // Set score cannot exceed 7 games in this format.
            if (p1Score > 7 || p2Score > 7) {
                return `Invalid score for Set ${i}. A player cannot win more than 7 games in a set.`;
            }

            // For retired matches, allow unfinished/partial sets.
            // We only count fully valid won sets and require at least one by final check.
            if (isOpponentRetired) {
                if (p1Score === 0 && p2Score === 0 && p1Super === 0 && p2Super === 0) {
                    continue;
                }

                // Completed non-tiebreak set
                if (isValidStraightSetWin(p1Score, p2Score, p1Super, p2Super)) {
                    p1SetsWon++;
                    continue;
                }
                if (isValidStraightSetWin(p2Score, p1Score, p2Super, p1Super)) {
                    p2SetsWon++;
                    continue;
                }

                // Completed tiebreak set
                if ((p1Score === 6 && p2Score === 6) || (p1Score === 7 && p2Score === 6) || (p1Score === 6 && p2Score === 7)) {
                    if (p1Super === 0 && p2Super === 0) {
                        continue; // unfinished tiebreak is acceptable in retired case
                    }
                    const tb = normalizeTiebreakScores(p1Score, p2Score, p1Super, p2Super);
                    if (!tb.ok) {
                        // Partial / unfinished TB entry — allow for retired
                        continue;
                    }
                    scores[`p1set${i}sup`] = tb.p1Sup;
                    scores[`p2set${i}sup`] = tb.p2Sup;
                    scores[`p1set${i}`] = tb.p1Won ? 7 : 6;
                    scores[`p2set${i}`] = tb.p1Won ? 6 : 7;
                    if (tb.p1Won) p1SetsWon++;
                    else p2SetsWon++;
                    continue;
                }

                // Explicit reject of illegal finished-looking 7-x (e.g. 7-4) even when retiring
                if (
                    (p1Score === 7 || p2Score === 7) &&
                    !(
                        (p1Score === 7 && p2Score === 5) ||
                        (p2Score === 7 && p1Score === 5) ||
                        (p1Score === 7 && p2Score === 6) ||
                        (p1Score === 6 && p2Score === 7)
                    )
                ) {
                    return `Invalid score for Set ${i}. When a set reaches 7, the only legitimate scores are 7-5 or 7-6 (with tiebreaker).`;
                }

                // Any other partial set is acceptable for retired result.
                continue;
            }

            // Handle regular set (no tiebreak): 6-0..6-4 or 7-5 only
            if (isValidStraightSetWin(p1Score, p2Score, p1Super, p2Super)) {
                p1SetsWon++;
            } else if (isValidStraightSetWin(p2Score, p1Score, p2Super, p1Super)) {
                p2SetsWon++;
            }
            // Handle tiebreak scenario (6-6 / 7-6)
            else if ((p1Score === 6 && p2Score === 6) || (p1Score === 7 && p2Score === 6) || (p1Score === 6 && p2Score === 7)) {
                const tb = normalizeTiebreakScores(p1Score, p2Score, p1Super, p2Super);
                if (!tb.ok) {
                    return `Set ${i}: ${tb.error}`;
                }
                // Persist deduced loser/winner TB (e.g. winner 9 → loser 7)
                scores[`p1set${i}sup`] = tb.p1Sup;
                scores[`p2set${i}sup`] = tb.p2Sup;
                scores[`p1set${i}`] = tb.p1Won ? 7 : 6;
                scores[`p2set${i}`] = tb.p1Won ? 6 : 7;
                if (tb.p1Won) p1SetsWon++;
                else p2SetsWon++;
            }
            // Illegal 7-x (e.g. 7-4, 7-3) — only 7-5 or 7-6(TB) are legitimate
            else if (
                (p1Score === 7 || p2Score === 7) &&
                !(
                    (p1Score === 7 && p2Score === 5) ||
                    (p2Score === 7 && p1Score === 5) ||
                    (p1Score === 7 && p2Score === 6) ||
                    (p1Score === 6 && p2Score === 7)
                )
            ) {
                return `Invalid score for Set ${i}. When a set reaches 7, the only legitimate scores are 7-5 or 7-6 (with tiebreaker).`;
            }
            // Invalid set score (neither player won, and no tiebreak)
            else if (p1Score > 0 || p2Score > 0) {
                return `Invalid score for Set ${i}. A player must win with at least 6 games and a 2-game margin, or a tiebreak is required at 6-6.`;
            }
        }

        // Ensure a winner is determined (best of 3 sets)
        // if (p1SetsWon < 2 && p2SetsWon < 2) {
        //     return "A player must win 2 sets. Please complete the necessary sets.";
        // }


        if (opponentRetired === "true") {
            const gamesCompleted =
                (Number(scores.p1set1) || 0) +
                (Number(scores.p2set1) || 0) +
                (Number(scores.p1set2) || 0) +
                (Number(scores.p2set2) || 0) +
                (Number(scores.p1set3) || 0) +
                (Number(scores.p2set3) || 0);
            if (gamesCompleted < 1) {
                return "At least one game must be completed to enter opponent retires.";
            }
        } else {
            if (p1SetsWon < 2 && p2SetsWon < 2) {
                return "A player must win 2 sets. Please complete the necessary sets.";
            }
        }

        return null; // No errors
    };

    const handleSubmit = async (e, skipValidation = false, overrideFormData = null) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        // Use overrideFormData if provided, otherwise use current formData
        const dataToUse = overrideFormData || formData;

        if (!skipValidation) {
            // const validationError = validateScores(formData.scores);
            const validationError = validateScores(
                dataToUse.scores,
                dataToUse.opponentRetired
            );
            if (validationError) {
                // Check if validationError is an object (for tiebreaker error)
                if (typeof validationError === 'object' && validationError.errorText) {
                    Modal.fire({
                        title: "Invalid Action",
                        errorText: validationError.errorText,
                        text: validationError.text,
                        icon: "error"
                        
                    });
                } else {
                    Modal.fire("Invalid Action", validationError, "error");
                }
                return;
            }
        }

        // Ensure boolean values are properly set as strings
        const payload = {
            ...dataToUse,
            bDefault: dataToUse.bDefault === "true" ? "true" : "false",
            opponentRetired: dataToUse.opponentRetired === "true" ? "true" : "false",
            bVerify: dataToUse.bVerify === "true" ? "true" : "false",
            playedDate: startDate
        };

        // If Default Victory is selected, add the logged-in user's ID as default_winner
        if (payload.bDefault === "true" && userInfo.userId) {
            payload.default_winner = userInfo.userId;
        }

        navigate(
            `/carter/schedule/viewresult?matchId=${matchId}&userId=${userId}&scheduleUserId=${scheduleUserId || userInfo.userId || userId}&seasonId=${seasonId || ''}`,
            {
                state: {
                    previewPayload: payload,
                    previewMeta: {
                        player1Name: matchPlayers.player1 || `${userInfo.firstName} ${userInfo.lastName}`.trim() || "Player 1",
                        player2Name: matchPlayers.player2 || opponentName || "Player 2",
                        // Must match golf_match.golfer1 / golfer2 (same order as p1*/p2* scores)
                        player1Id: matchPlayers.golfer1Id ?? null,
                        player2Id: matchPlayers.golfer2Id ?? null,
                        // Person entering scores wins when opponent retires; URL userId = opponent
                        enteringUserId: userInfo.userId || null,
                        opponentId: userId ? Number(userId) : null,
                        seasonName: matchSeasonName || "",
                        playBy: startDate,
                        returnTo: location.search
                    }
                }
            }
        );
    };

    const handleDefaultVictory = (e) => {
        e.preventDefault();
        Modal.fire({
            title: `${userInfo.firstName} ${userInfo.lastName} won by default.`,
            text: `Do you want to declare a ${userInfo.firstName} ${userInfo.lastName} Default Victory?`,
            imageUrl: "/images/trophy-icon.png",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Confirm!",
        }).then((result) => {
            if (result.isConfirmed) {
                // Create updated formData with bDefault set to true
                const updatedFormData = {
                    ...formData,
                    bDefault: "true"
                };
                
                // Update state for UI
                setFormData(updatedFormData);
                
                // Call handleSubmit with updated formData immediately (don't wait for state update)
                handleSubmit(null, true, updatedFormData);
            }
        });
    };

    const handleOpponentRetirement = (e) => {
        e.preventDefault();


        if (formData.opponentRetired !== "true") {
            Modal.fire({
                title: "Checkbox Required",
                text: "Please check the 'Opponent Retired' checkbox before proceeding.",
                icon: "warning",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "OK"
            });
            return;
        }

        const p1SetsWon = Object.values(formData.scores).filter((_, index) => index % 2 === 0 && formData.scores[`p1set${(index / 2) + 1}`] >= 6).length;
        const p2SetsWon = Object.values(formData.scores).filter((_, index) => index % 2 !== 0 && formData.scores[`p2set${Math.floor(index / 2) + 1}`] >= 6).length;

        if (p2SetsWon >= 2) {
            Modal.fire("Error", "Opponent cannot retire after winning 2 sets. Please post the score.", "error");
            return;
        }

        Modal.fire({
            title: "Confirm Opponent Retirement",
            text: `${userInfo.firstName} ${userInfo.lastName} wins as opponent retired. Do you want to proceed?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Confirm",
        }).then((result) => {
            if (result.isConfirmed) {
                const updatedFormData = { ...formData, opponentRetired: "true" };
                setFormData(updatedFormData);
                handleSubmit(null, false, updatedFormData);
            }
        });
    };



    return (
        <div className="page-container col-sm-9 mb-5">
            <div className="section section-post">
                <div className="left">
                    <h4 className='view-result-title'>{editMode ? 'Edit Match Results' : 'Post Your Results'}</h4>
                    {editMode && userInfo.role === 1 && (
                        <p className='text-light' style={{ fontSize: '14px', marginTop: '5px' }}>
                            (Admin Edit Mode)
                        </p>
                    )}
                </div>
            </div>
            <form id="matchForm" onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-sm-4">
                        <div className="section">
                            <div id="played-info">
                                <div className=" heading-golfer">
                                    <label htmlFor="played">Date Played</label>
                                    <img src="/images/date-played.png" alt="" />
                                </div>
                                <div>
                                </div>
                                <div>
                                    <Calendar 
                                        value={startDate}
                                        onChange={(date) => setStartDate(date)}
                                        style={{ width: '100%' }}
                                        maxDate={new Date()} 
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                    <div className="col-lg-8 mb-3">
                        <div className="section postScoreSection">
                            <div className=" heading-golfer" >
                                <label className=''> Post Scores</label>
                                <img src="/images/score.png" alt="" />
                            </div>



                            <div className="card bg-light">
                                <div className="card-body post_scores_table">
                                    <div className='table-responsive'>
                                        <table className='table text-nowrap'>
                                            <tbody>
                                            <tr>
                                                <td>
                                                    <img
                                                        className="align-middle"
                                                        style={{ width: 50 }}
                                                        src="/images/icon-12.png"
                                                    />
                                                    <b> {matchPlayers.player1 || `${userInfo.firstName} ${userInfo.lastName}`} &nbsp;</b>

                                                </td>
                                                <td>
                                                    <input
                                                        id="p1set1"
                                                        name="scores.p1set1"
                                                        className="num-input"
                                                        type="text"
                                                        value={formData.scores.p1set1}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={1}
                                                        tabIndex={1}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                    <input
                                                        id="p1set1sup"
                                                        name="scores.p1set1sup"
                                                        className="super-input"
                                                        type="text"
                                                        value={formData.scores.p1set1sup}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={2}
                                                        tabIndex={3}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        id="p1set2"
                                                        name="scores.p1set2"
                                                        className="num-input"
                                                        type="text"
                                                        value={formData.scores.p1set2}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={1}
                                                        tabIndex={5}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                    <input
                                                        id="p1set2sup"
                                                        name="scores.p1set2sup"
                                                        className="super-input"
                                                        type="text"
                                                        value={formData.scores.p1set2sup}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={2}
                                                        tabIndex={7}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />


                                                </td>
                                                <td>
                                                    <input
                                                        id="p1set3"
                                                        name="scores.p1set3"
                                                        className="num-input"
                                                        type="text"
                                                        value={formData.scores.p1set3}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={1}
                                                        tabIndex={9}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                    <input
                                                        id="player1set3sup"
                                                        name="scores.p1set3sup"
                                                        className="super-input"
                                                        type="text"
                                                        value={formData.scores.p1set3sup}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={2}
                                                        tabIndex={11}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />


                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <img
                                                        className="align-middle"
                                                        style={{ width: 50 }}
                                                        src="/images/icon-12.png"
                                                    />
                                                    <b className='ml-2'>{matchPlayers.player2 || opponentName} &nbsp;</b>
                                                </td>
                                                <td>
                                                    <input
                                                        id="p2set1"
                                                        name="scores.p2set1"
                                                        className="num-input"
                                                        type="text"
                                                        value={formData.scores.p2set1}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={1}
                                                        tabIndex={2}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                    <input
                                                        id="p2set1sup"
                                                        name="scores.p2set1sup"
                                                        className="super-input-bottom"
                                                        type="text"
                                                        defaultValue={0}
                                                        value={formData.scores.p2set1sup}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        maxLength={2}
                                                        tabIndex={4}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        id="p2set2"
                                                        name="scores.p2set2"
                                                        className="num-input"
                                                        type="text"
                                                        value={formData.scores.p2set2}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={1}
                                                        tabIndex={6}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                    <input
                                                        id="p2set2sup"
                                                        name="scores.p2set2sup"
                                                        className="super-input-bottom"
                                                        type="text"
                                                        value={formData.scores.p2set2sup}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={2}
                                                        tabIndex={8}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        id="p2set3"
                                                        name="scores.p2set3"
                                                        className="num-input"
                                                        type="text"
                                                        value={formData.scores.p2set3}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={1}
                                                        tabIndex={10}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                    <input
                                                        id="p2set3sup"
                                                        name="scores.p2set3sup"
                                                        className="super-input-bottom"
                                                        type="text"
                                                        value={formData.scores.p2set3sup}
                                                        onChange={handleInputChange}
                                                        onFocus={handleScoreFocus}
                                                        defaultValue={0}
                                                        maxLength={2}
                                                        tabIndex={12}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                    />
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <div className="opponentSection m-0 bg-transparent py-0">
                                        {/* Changes for Opponent Required */}
                                        <div className="onecolw">
                                            <fieldset>
                                                <input
                                                    id="opponentRetired"
                                                    name="opponentRetired"
                                                    type="checkbox"
                                                    checked={formData.opponentRetired === "true"}
                                                    onChange={handleInputChange}
                                                />
                                                <input
                                                    type="hidden"
                                                    defaultValue={1}
                                                    name="_score.opponentRetired"
                                                />
                                                <label className="checkboxlabel">Opponent Retired</label>
                                            </fieldset>
                                        </div>
                                        <div className="onecolw">
                                            <Link to="#" className="msg-link" onClick={handleDefaultVictory} >
                                                Default Victory
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            
                        </div>
                    </div>
                </div>
                <div className="clear" />
                <div className="section" style={{ marginTop: 40 }}>
                    <div className="buttons">
                        <div className="cancel-btn left">
                            <div className="buttonwrapper">
                                <Link
                                    to={
                                        editMode && returnPage === 'members-record'
                                            ? { pathname: '/carter/members/record', state: { memberId: returnMemberId || userId, recordType: returnRecordType } }
                                            : editMode && returnPage === 'view-result'
                                            ? `/carter/schedule/viewresult?matchId=${matchId}&userId=${userId}&scheduleUserId=${scheduleUserId || userId || ''}&seasonId=${seasonId || ''}`
                                            : `/carter/schedule?userId=${scheduleUserId || userId || ''}`
                                    }
                                    className="squarebutton"
                                >
                                    <span>Cancel</span>
                                </Link>
                            </div>
                        </div>
                        <div className="post-btn left">
                            <div className="buttonwrapper">
                                <Link to="#"
                                    className="squarebutton"
                                    onClick={handleSubmit}
                                >
                                    <span>Save</span>
                                </Link>
                            </div>
                        </div>
                        {/* <div className="post-btn left">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={openModal}
                            >
                                Launch demo modal
                            </button>
                        </div> */}
                        <div className="clear" />
                        <input id="bCancel" type="hidden" name="bCancel" defaultValue="false" />
                        <input
                            id="bDefault"
                            type="hidden"
                            name="bDefault"
                            defaultValue="false"
                        />
                        <input id="bVerify" type="hidden" name="bVerify" defaultValue="false" />
                        <div className="clear" />
                    </div>
                </div>
            </form>

            {/* Bootstrap 4 Modal */}


            <style>{`
                .react-calendar {
                    width: 100% !important;
                    max-width: 100%;
                    background: white;
                    border: 1px solid #a0a096;
                    font-family: 'Arial', 'Helvetica', sans-serif;
                    line-height: 1.125em;
}
            `}</style>

        </div>

    )
}

export default PostResult